



import React, { useState, useEffect } from 'react';
import {
  Box,
  Heading,
  Input,
  FormControl,
  FormLabel,
  Button,
  VStack,
  useToast,
  Spinner,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  useDisclosure,
  Flex,
  Text,
} from '@chakra-ui/react';
import axios from 'axios';

export default function ContactUsAdmin() {
  const toast = useToast();
  const [loading, setLoading] = useState(false);
  const { isOpen, onOpen, onClose } = useDisclosure();

  const [contactData, setContactData] = useState({
    email: '',
    phoneNumber: '',
    location: '',
    socialMedia: {
      facebook: '',
      instagram: '',
      twitter: '',
      linkedin: '',
    },
  });

  // --- BASE URL from env ---
  const RAW_BASE_URL =
    process.env.REACT_APP_BASE_URL?.replace(/\/$/, '') ||
    'http://localhost:3030';
  const API_BASE = `${RAW_BASE_URL}/api/admin`;

  const getToken = () =>
    localStorage.getItem('adminToken') || localStorage.getItem('token');

  const fetchContactData = async () => {
    const token = getToken();
    if (!token) {
      toast({
        title: 'Unauthorized',
        description: 'Please login as admin to view contact details',
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
      return;
    }
    try {
      setLoading(true);
      const res = await axios.get(`${API_BASE}/contact-us`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.data) setContactData(res.data);
    } catch (err) {
      toast({
        title: 'Fetch failed',
        description: err.response?.data?.message || err.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchContactData();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (['facebook', 'instagram', 'twitter', 'linkedin'].includes(name)) {
      setContactData({
        ...contactData,
        socialMedia: { ...contactData.socialMedia, [name]: value },
      });
    } else {
      setContactData({ ...contactData, [name]: value });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const token = getToken();
    if (!token) {
      toast({
        title: 'Unauthorized',
        description: 'Please login as admin to save contact details',
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
      return;
    }

    try {
      setLoading(true);
      const method = contactData._id ? 'put' : 'post';
      const headers = { Authorization: `Bearer ${token}` };
      const url = `${API_BASE}/contact-us`;

      await axios({ method, url, data: contactData, headers });

      toast({
        title: 'Success',
        description: 'Contact details saved!',
        status: 'success',
        duration: 4000,
        isClosable: true,
      });

      await fetchContactData();
      onClose();
    } catch (err) {
      const message =
        err.response?.status === 401
          ? 'Please login as admin to save details'
          : err.response?.data?.message || err.message;
      toast({
        title: 'Save failed',
        description: message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  if (loading)
    return (
      <Flex justify="center" align="center" mt={20}>
        <Spinner size="xl" />
      </Flex>
    );

  return (
    <Box maxW="900px" mx="auto" mt={10} p={6} bg="white" borderRadius="lg" boxShadow="lg">
      <Heading mb={6} textAlign="center" fontSize="2xl">
        Admin Contact Us Settings
      </Heading>

      {/* Single Edit Button */}
      {contactData._id && (
        <Flex justify="flex-end" mb={4}>
          <Button colorScheme="green" onClick={onOpen}>
            Edit Contact Details
          </Button>
        </Flex>
      )}

      {/* Contact Details Table */}
      {contactData._id ? (
        <Table variant="striped" colorScheme="gray" size="md">
          <Thead bg="blue.50">
            <Tr>
              <Th>Field</Th>
              <Th>Value</Th>
            </Tr>
          </Thead>
          <Tbody>
            <Tr>
              <Td>Email</Td>
              <Td>{contactData.email}</Td>
            </Tr>
            <Tr>
              <Td>Phone Number</Td>
              <Td>{contactData.phoneNumber}</Td>
            </Tr>
            <Tr>
              <Td>Location</Td>
              <Td>{contactData.location}</Td>
            </Tr>
            {Object.entries(contactData.socialMedia).map(([key, value]) => (
              <Tr key={key}>
                <Td>{key.charAt(0).toUpperCase() + key.slice(1)}</Td>
                <Td>{value || 'N/A'}</Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      ) : (
        <Text textAlign="center" mt={10} color="gray.500">
          No contact details available. Click "Edit Contact Details" to create.
        </Text>
      )}

      {/* Modal Form */}
      <Modal isOpen={isOpen} onClose={onClose} size="lg" isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Edit Contact Details</ModalHeader>
          <ModalCloseButton />
          <form onSubmit={handleSubmit}>
            <ModalBody>
              <VStack spacing={4} align="stretch">
                <FormControl>
                  <FormLabel>Email</FormLabel>
                  <Input
                    type="email"
                    name="email"
                    value={contactData.email}
                    onChange={handleChange}
                    required
                  />
                </FormControl>

                <FormControl>
                  <FormLabel>Phone Number</FormLabel>
                  <Input
                    type="tel"
                    name="phoneNumber"
                    value={contactData.phoneNumber}
                    onChange={handleChange}
                    required
                  />
                </FormControl>

                <FormControl>
                  <FormLabel>Location / Address</FormLabel>
                  <Input
                    type="text"
                    name="location"
                    value={contactData.location}
                    onChange={handleChange}
                    required
                  />
                </FormControl>

                <Heading size="sm" mt={4} mb={2}>
                  Social Media Links
                </Heading>

                {Object.keys(contactData.socialMedia).map((key) => (
                  <FormControl key={key}>
                    <FormLabel>{key.charAt(0).toUpperCase() + key.slice(1)}</FormLabel>
                    <Input
                      type="text"
                      name={key}
                      value={contactData.socialMedia[key]}
                      onChange={handleChange}
                      placeholder={`Enter ${key} URL`}
                    />
                  </FormControl>
                ))}
              </VStack>
            </ModalBody>

            <ModalFooter>
              <Button variant="ghost" mr={3} onClick={onClose}>
                Cancel
              </Button>
              <Button colorScheme="green" type="submit" isLoading={loading}>
                Save
              </Button>
            </ModalFooter>
          </form>
        </ModalContent>
      </Modal>
    </Box>
  );
}
